import React from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { GradientButton, SERIF, TOKENS } from "./primitives";

/**
 * Shared marketing shell for every public page (/, /pricing, /security,
 * /privacy, /terms, /refund, /cookies, /brochure, /status, /docs).
 *
 * Warm editorial system: cream paper canvas, near-black ink, a floating
 * pill nav detached from the top edge, and a footer that ends in a giant
 * wordmark. Pages build their rhythm from full-width rounded color panels
 * (see Panel in primitives) rather than borders on a dark void.
 *
 * The shell forces LIGHT on the document while mounted (the dashboard
 * follows the user's stored theme preference and is unaffected after
 * unmount).
 */

const NAV_LINKS = [
  { label: "Product",    to: "/#product" },
  { label: "Why Paxley", to: "/why-paxley" },
  { label: "Code Trust", to: "/code-trust" },
  { label: "Pricing",    to: "/pricing" },
  { label: "Insights",   to: "/insights" },
  { label: "Docs",       to: "/docs" },
];

const FOOTER_COLS = [
  {
    title: "Product",
    links: [
      { label: "What it finds", to: "/#product" },
      { label: "Why Paxley",    to: "/why-paxley" },
      { label: "Code Trust",    to: "/code-trust" },
      { label: "How it works",  to: "/#how-it-works" },
      { label: "Pricing",       to: "/pricing" },
      { label: "Status",        to: "/status" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Insights",      to: "/insights" },
      { label: "Documentation", to: "/docs" },
      { label: "Brochure",      to: "/brochure" },
      { label: "Security",      to: "/security" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Request a quote", to: "/inquiry" },
      { label: "Support",       href: "mailto:support@paxleysoftware.com" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy",       to: "/privacy" },
      { label: "Terms",         to: "/terms" },
      { label: "Refund policy", to: "/refund" },
      { label: "Cookie policy", to: "/cookies" },
    ],
  },
];

type FooterLink = { label: string; to?: string; href?: string };

export default function MarketingLayout({ children }: { children: React.ReactNode }) {
  React.useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains("dark");
    root.classList.remove("dark");
    return () => {
      if (wasDark) root.classList.add("dark");
    };
  }, []);

  return (
    // overflow-x-clip: no marketing page may ever scroll horizontally,
    // whatever a section does. clip (not hidden) so position:sticky inside
    // pages keeps working.
    <div
      className="relative min-h-screen overflow-x-clip antialiased"
      style={{ backgroundColor: TOKENS.paper, color: TOKENS.ink }}
    >
      <MarketingNav />
      <main className="relative">{children}</main>
      <MarketingFooter />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Nav — floating pill, detached from the top edge
// ──────────────────────────────────────────────────────────────────

function MarketingNav() {
  const [open, setOpen] = React.useState(false);
  const { pathname } = useLocation();

  React.useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header className="fixed inset-x-0 top-4 z-50 px-3 sm:px-5">
        <nav className="mx-auto flex max-w-5xl items-center justify-between rounded-2xl border border-[#17150F]/15 bg-white/90 px-4 py-2.5 shadow-[0_2px_16px_rgba(23,21,15,0.06)] backdrop-blur sm:px-5">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="" className="h-8 w-8 rounded-lg" />
            <span className="flex flex-col leading-none">
              <span className="text-[17px] font-semibold tracking-tight">Paxley</span>
              <span className="mt-0.5 text-[8.5px] font-semibold uppercase tracking-[0.14em] opacity-50">
                Code security
              </span>
            </span>
          </Link>

          <div className="hidden items-center gap-1 md:flex">
            {NAV_LINKS.map((link) => (
              <NavLinkItem key={link.to} to={link.to} current={pathname === link.to}>
                {link.label}
              </NavLinkItem>
            ))}
          </div>

          <div className="hidden items-center gap-2 md:flex">
            <Link
              to="/login"
              className="rounded-xl px-3.5 py-2 text-sm font-medium opacity-70 transition-opacity hover:opacity-100"
            >
              Sign in
            </Link>
            <GradientButton to="/onboarding">Start free scan</GradientButton>
          </div>

          <button
            className="rounded-lg border border-[#17150F]/15 p-2 md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-x-4 top-[4.6rem] z-50 rounded-2xl border border-[#17150F]/15 bg-white p-4 shadow-lg md:hidden"
          >
            <div className="flex flex-col">
              {NAV_LINKS.map((link) => (
                <NavAnchor
                  key={link.to}
                  to={link.to}
                  className="rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-[#FBF8F1]"
                >
                  {link.label}
                </NavAnchor>
              ))}
              <NavAnchor to="/login" className="rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-[#FBF8F1]">
                Sign in
              </NavAnchor>
            </div>
            <div className="mt-3 border-t border-[#E4DFD3] pt-3">
              <GradientButton to="/onboarding" className="w-full justify-center">
                Start free scan
              </GradientButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function NavAnchor({
  to,
  className,
  children,
}: {
  to: string;
  className: string;
  children: React.ReactNode;
}) {
  if (to.includes("#")) {
    return (
      <a href={to} className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

function NavLinkItem({
  to,
  current,
  children,
}: {
  to: string;
  current: boolean;
  children: React.ReactNode;
}) {
  const className = `rounded-xl px-3.5 py-2 text-sm font-medium transition-colors ${
    current ? "bg-[#FBF8F1]" : "opacity-70 hover:opacity-100"
  }`;
  return (
    <NavAnchor to={to} className={className}>
      {children}
    </NavAnchor>
  );
}

// ──────────────────────────────────────────────────────────────────
// Footer — link columns, then the wordmark
// ──────────────────────────────────────────────────────────────────

function MarketingFooter() {
  return (
    <footer className="border-t border-[#E4DFD3]">
      <div className="mx-auto max-w-7xl px-5 pb-10 pt-16 sm:px-8">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-6">
          <div className="col-span-2">
            <p className="max-w-xs text-sm leading-relaxed opacity-70">
              Paxley unifies code security, protects the software supply chain and proves code
              trust with evidence, for engineering teams, regulated software makers, and investors
              assessing a codebase before they buy it.
            </p>
            <p className="mt-5 text-xs opacity-50">
              yash.sancheti@paxleysoftware.com
            </p>
          </div>

          {FOOTER_COLS.map((col) => (
            <div key={col.title}>
              <h4 className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-50">
                {col.title}
              </h4>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((link: FooterLink) => (
                  <li key={link.label}>{renderFooterLink(link)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col justify-between gap-2 border-t border-[#E4DFD3] pt-6 text-xs opacity-60 sm:flex-row">
          <p>© {new Date().getFullYear()} Paxley · Nucleus Systems. All rights reserved.</p>
          <Link to="/status" className="hover:opacity-100">
            Service status
          </Link>
        </div>

        <p
          aria-hidden="true"
          className="mt-10 select-none text-center text-[22vw] leading-[0.8] tracking-[-0.03em] opacity-[0.07] md:text-[17rem]"
          style={SERIF}
        >
          Paxley
        </p>
      </div>
    </footer>
  );
}

function renderFooterLink(link: FooterLink) {
  const base = "text-sm opacity-75 transition-opacity hover:opacity-100";
  if (link.href) {
    return (
      <a href={link.href} className={base}>
        {link.label}
      </a>
    );
  }
  return (
    <Link to={link.to!} className={base}>
      {link.label}
    </Link>
  );
}

export { MarketingNav, MarketingFooter };
